/**
 * Domain Configuration System
 *
 * Supports multi-domain branding for terr.ai and fixd.ai
 * Both domains share the same backend but have different branding and messaging
 */

export type DomainType = 'fixdai' | 'terrai';

export interface DomainConfig {
  type: DomainType;
  name: string;
  displayName: string;
  domain: string;

  // Branding
  primaryColor: string;
  secondaryColor: string;
  logo: string;
  favicon: string;

  // SEO
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string[];

  // Features
  showLogin: boolean;
  showPropertyManagement: boolean;
  showSaveTogether: boolean;

  // Messaging
  heroTitle: string;
  heroSubtitle: string;
  ctaText: string;

  // Legal
  termsUrl?: string;
  privacyUrl?: string;
}

const TERRAI_CONFIG: DomainConfig = {
  type: 'terrai',
  name: 'terrai',
  displayName: 'Terr.ai',
  domain: 'terr.ai',

  primaryColor: '#9D1500',
  secondaryColor: '#c72b2b',
  logo: '/assets/brands/terrai/logo.svg',
  favicon: '/assets/brands/terrai/favicon.ico',

  metaTitle: 'Terr.ai - AI-Powered Property Management & Vendor Search',
  metaDescription:
    'Simplify property management with AI. Find trusted vendors, manage maintenance, and get instant quotes for your property needs.',
  metaKeywords: [
    'property management',
    'vendor search',
    'home maintenance',
    'AI quotes',
    'property maintenance',
    'landlord tools',
  ],

  showLogin: true,
  showPropertyManagement: true,
  showSaveTogether: true,

  heroTitle: 'AI-Powered Property Management Made Simple',
  heroSubtitle:
    'Find trusted vendors, get instant quotes, and manage your properties with AI assistance.',
  ctaText: 'Find Vendors Now',

  termsUrl: '/terms',
  privacyUrl: '/privacy',
};

const FIXDAI_CONFIG: DomainConfig = {
  type: 'fixdai',
  name: 'fixdai',
  displayName: 'Fixd.ai',
  domain: 'fixd.ai',

  primaryColor: '#FF7F2B',
  secondaryColor: '#2C3E50',
  logo: '/assets/brands/fixdai/logo.svg',
  favicon: '/assets/brands/fixdai/favicon.ico',

  metaTitle: 'Fixd.ai - Find Local Home Repair Contractors & Get Instant Quotes',
  metaDescription:
    'Find licensed contractors for home repairs. Get instant quotes from local professionals. Plumbers, electricians, HVAC, and more.',
  metaKeywords: [
    'home repair',
    'local contractors',
    'plumber near me',
    'electrician near me',
    'HVAC repair',
    'home maintenance',
    'contractor quotes',
    'handyman services',
  ],

  showLogin: false,
  showPropertyManagement: false,
  showSaveTogether: false,

  heroTitle: 'Find Local Contractors. Get Instant Quotes.',
  heroSubtitle: 'Connect with licensed professionals for home repairs in your area.',
  ctaText: 'Search Contractors',

  termsUrl: '/terms',
  privacyUrl: '/privacy',
};

/**
 * Get domain configuration based on hostname
 * Supports localhost for development
 */
export const getDomainConfig = (hostname?: string): DomainConfig => {
  const host = hostname || (typeof window !== 'undefined' ? window.location.hostname : '');

  // Check for terr.ai domain
  if (host.includes('terr.ai') || host.includes('terr.localhost')) {
    return TERRAI_CONFIG;
  }

  // Default to fixd.ai
  return FIXDAI_CONFIG;
};

/**
 * Get domain type from hostname
 */
export const getDomainType = (hostname?: string): DomainType => getDomainConfig(hostname).type;

/**
 * Check if current domain is Fixd.ai
 */
export const isFixdDomain = (hostname?: string): boolean => getDomainType(hostname) === 'fixdai';

/**
 * Check if current domain is Terr.ai
 */
export const isTerraiDomain = (hostname?: string): boolean => getDomainType(hostname) === 'terrai';

/**
 * Get logo path for current domain
 */
export const getDomainLogo = (hostname?: string): string => getDomainConfig(hostname).logo;

/**
 * Get primary color for current domain
 */
export const getDomainPrimaryColor = (hostname?: string): string =>
  getDomainConfig(hostname).primaryColor;
